<script setup lang="ts">
/**
 * Public marketing top nav (DESIGN.md primary-nav).
 *
 * Layout: brand (left) · section links (Models/Docs/Pricing) · right cluster.
 * Right cluster is auth-aware:
 *   - guest  → "Sign in" (secondary) + black "Get API key" pill
 *   - authed → "Open dashboard" pill
 * Mobile: links collapse into a drawer; the CTA pill never collapses.
 */
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAuthStore } from '../stores/auth';

const auth = useAuthStore();
const menuOpen = ref(false);

const links = [
  { to: '/models', label: 'Models' },
  { to: '/docs', label: 'Docs' },
  { to: '/#pricing', label: 'Pricing' },
];
</script>

<template>
  <header class="bg-canvas border-b border-hairline">
    <nav class="max-w-6xl mx-auto h-14 px-4 lg:px-6 flex items-center gap-4">
      <!-- Brand -->
      <RouterLink
        to="/"
        class="text-ink shrink-0 focus-ring rounded-md font-display font-bold text-lg tracking-tight"
      >Open Gonka</RouterLink>

      <!-- Desktop section links -->
      <div class="hidden md:flex items-center gap-1 ml-2">
        <a
          v-for="l in links"
          :key="l.to"
          :href="l.to"
          class="px-3 py-1.5 text-sm font-medium text-body hover:text-ink rounded-full hover:bg-surface-soft transition"
          >{{ l.label }}</a
        >
      </div>

      <div class="flex-1" />

      <!-- Right cluster -->
      <div class="hidden sm:flex items-center gap-2">
        <template v-if="auth.me">
          <RouterLink to="/dashboard" class="btn-primary">Open dashboard</RouterLink>
        </template>
        <template v-else>
          <RouterLink to="/login" class="btn-secondary">Sign in</RouterLink>
          <RouterLink to="/login" class="btn-primary">Get API key</RouterLink>
        </template>
      </div>

      <!-- Mobile menu toggle -->
      <button
        type="button"
        class="md:hidden p-2 -mr-2 rounded-full text-ink hover:bg-surface-soft focus-ring"
        :aria-expanded="menuOpen"
        aria-label="Toggle menu"
        @click="menuOpen = !menuOpen"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
    </nav>

    <!-- Mobile drawer -->
    <div v-if="menuOpen" class="md:hidden border-t border-hairline px-4 py-3 flex flex-col gap-1">
      <a
        v-for="l in links"
        :key="l.to"
        :href="l.to"
        class="px-3 py-2 text-sm font-medium text-body hover:text-ink rounded-full hover:bg-surface-soft"
        @click="menuOpen = false"
        >{{ l.label }}</a
      >
      <div class="flex gap-2 pt-2">
        <template v-if="auth.me">
          <RouterLink to="/dashboard" class="btn-primary flex-1" @click="menuOpen = false">Open dashboard</RouterLink>
        </template>
        <template v-else>
          <RouterLink to="/login" class="btn-secondary flex-1" @click="menuOpen = false">Sign in</RouterLink>
          <RouterLink to="/login" class="btn-primary flex-1" @click="menuOpen = false">Get API key</RouterLink>
        </template>
      </div>
    </div>
  </header>
</template>
